/**
 * Waveform-seed pass. Additively writes a glowing ribbon onto the current
 * feedback target. Owns its own pipeline, params buffer, and waveform buffer.
 */

import seedWgsl from '../shaders/waveform_seed.wgsl?raw';

export const WAVEFORM_SAMPLE_COUNT = 1024;

export type SeedParams = {
  baseY: number;
  gain: number;
  halfThick: number;
  intensity: number;
  color: [number, number, number];
  edgeSharp: number;
};

const SEED_PARAMS_BYTES = 32; // baseY,gain,halfThick,intensity, color3+pad, edgeSharp+pad

export class WaveformSeedPass {
  private readonly device: GPUDevice;
  private readonly pipeline: GPURenderPipeline;
  private readonly waveformBuffer: GPUBuffer;
  private readonly seedParamsBuffer: GPUBuffer;
  private readonly bindGroup: GPUBindGroup;
  private readonly cpuParams = new ArrayBuffer(SEED_PARAMS_BYTES);
  private readonly cpuParamsF32 = new Float32Array(this.cpuParams);
  private readonly cpuWaveform = new Float32Array(WAVEFORM_SAMPLE_COUNT);

  constructor(
    device: GPUDevice,
    targetFormat: GPUTextureFormat,
    frameUniformsBuffer: GPUBuffer,
  ) {
    this.device = device;

    this.waveformBuffer = device.createBuffer({
      label: 'waveform-storage',
      size: WAVEFORM_SAMPLE_COUNT * 4,
      usage: GPUBufferUsage.STORAGE | GPUBufferUsage.COPY_DST,
    });

    this.seedParamsBuffer = device.createBuffer({
      label: 'seed-params',
      size: SEED_PARAMS_BYTES,
      usage: GPUBufferUsage.UNIFORM | GPUBufferUsage.COPY_DST,
    });

    const shader = device.createShaderModule({
      label: 'waveform-seed',
      code: seedWgsl,
    });

    const layout = device.createBindGroupLayout({
      label: 'seed-bgl',
      entries: [
        {
          binding: 0,
          visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT,
          buffer: { type: 'uniform' },
        },
        {
          binding: 1,
          visibility: GPUShaderStage.VERTEX,
          buffer: { type: 'read-only-storage' },
        },
        {
          binding: 2,
          visibility: GPUShaderStage.VERTEX | GPUShaderStage.FRAGMENT,
          buffer: { type: 'uniform' },
        },
      ],
    });

    this.pipeline = device.createRenderPipeline({
      label: 'waveform-seed-pipeline',
      layout: device.createPipelineLayout({ bindGroupLayouts: [layout] }),
      vertex: { module: shader, entryPoint: 'vs_main' },
      fragment: {
        module: shader,
        entryPoint: 'fs_main',
        targets: [
          {
            format: targetFormat,
            blend: {
              color: { srcFactor: 'one', dstFactor: 'one', operation: 'add' },
              alpha: { srcFactor: 'zero', dstFactor: 'one', operation: 'add' },
            },
            writeMask: GPUColorWrite.ALL,
          },
        ],
      },
      primitive: { topology: 'triangle-strip' },
    });

    this.bindGroup = device.createBindGroup({
      label: 'seed-bg',
      layout,
      entries: [
        { binding: 0, resource: { buffer: frameUniformsBuffer } },
        { binding: 1, resource: { buffer: this.waveformBuffer } },
        { binding: 2, resource: { buffer: this.seedParamsBuffer } },
      ],
    });
  }

  uploadWaveform(samples: Float32Array): void {
    const n = Math.min(samples.length, WAVEFORM_SAMPLE_COUNT);
    // Copy through an owned ArrayBuffer-backed Float32Array. This keeps the
    // type concrete (Float32Array<ArrayBuffer>, not <ArrayBufferLike>) and
    // sidesteps SharedArrayBuffer incompatibilities for writeBuffer.
    if (n === WAVEFORM_SAMPLE_COUNT) {
      this.cpuWaveform.set(samples);
    } else {
      this.cpuWaveform.fill(0);
      this.cpuWaveform.set(samples.subarray(0, n));
    }
    this.device.queue.writeBuffer(this.waveformBuffer, 0, this.cpuWaveform);
  }

  updateParams(p: SeedParams): void {
    const f = this.cpuParamsF32;
    f[0] = p.baseY;
    f[1] = p.gain;
    f[2] = p.halfThick;
    f[3] = p.intensity;
    f[4] = p.color[0];
    f[5] = p.color[1];
    f[6] = p.color[2];
    f[7] = p.edgeSharp;
    this.device.queue.writeBuffer(this.seedParamsBuffer, 0, this.cpuParams);
  }

  encode(encoder: GPUCommandEncoder, targetView: GPUTextureView): void {
    const pass = encoder.beginRenderPass({
      label: 'waveform-seed-pass',
      colorAttachments: [
        {
          view: targetView,
          loadOp: 'load',
          storeOp: 'store',
        },
      ],
    });
    pass.setPipeline(this.pipeline);
    pass.setBindGroup(0, this.bindGroup);
    pass.draw(WAVEFORM_SAMPLE_COUNT * 2, 1, 0, 0);
    pass.end();
  }
}
